import { blinkConstants } from '@thoughtspot/blink-constants';
import { DEFAULT_DATASOURCE_ID } from '@thoughtspot/custom-calendar-service';
import { FEATURE_FLAG_NAMES } from '@thoughtspot/feature-service';
import { getCurrentLocale } from '@thoughtspot/i18n';
import * as SessionService from '@thoughtspot/session-service';
import { getEmptyValuePlaceHolder, getNullValuePlaceHolder, } from '@thoughtspot/special-data-value-format';
import { dateFormats, strings } from '@thoughtspot/translations';
import { getFlagsFromEventData } from '../events/chart-to-ts/native-chart-events/flag-events/utils';
import { getLDFlagsFromEventData } from '../events/chart-to-ts/native-chart-events/get-ld-flags/utils';
import { getAllFontFaces, getNumOfColorPalette } from './style-config.util';
export const getSelectedUserProperties = (userProperties) => {
    if (!userProperties) {
        return {};
    }
    return {
        persona: userProperties?.persona,
    };
};
const fontFacesCache = new Map();
export const generateAppConfig = async (isPrintMode, isLiveboardContext, customChartAppUrl, customStylingService, chartAppAccessToken = '', embedCssStyles = {}, isTsHostedChart, queryMetrics, isEmbedded = false, isSpotterContext = false) => {
    const blinkFlags = getFlagsFromEventData([
        'isChartSettingsV2Enabled',
        'isHighchartCustomisationEnabled',
        'smartSortV2Enabled',
        'hideChartComponentsForSmallViewport',
        'muzeStudioRowsLimit',
        'isDynamicTitleSupported',
        'muzeStudioAiEnabled',
    ]);
    const featureFlags = [];
    if (isTsHostedChart) {
        featureFlags.push(FEATURE_FLAG_NAMES.ENABLE_ADVANCED_PIVOT_TABLE_TOTALS_BY_DEFAULT);
        featureFlags.push(FEATURE_FLAG_NAMES.ENABLE_CLIENT_SIDE_MN_MV_CALCULATION);
    }
    const ldFlags = getLDFlagsFromEventData(featureFlags);
    const [customCalendars, releaseVersion, debugMode, locale, currencyLocale, timezone, quarterStart, numberFormatLocale, dateFormatLocale,] = await Promise.all([
        Promise.resolve(SessionService.getAvailableCustomCalendars()),
        Promise.resolve(SessionService?.getReleaseVersion()),
        Promise.resolve(SessionService?.isInDebugMode() ?? false),
        Promise.resolve(getCurrentLocale()),
        Promise.resolve(SessionService.getPreferredCurrencyFormat()),
        Promise.resolve(SessionService.getSessionTimeZone()),
        Promise.resolve(SessionService.getQuarterStartMonth()),
        Promise.resolve(SessionService.getFormatLocales().numberFormatLocale),
        Promise.resolve(SessionService.getFormatLocales().dateFormatLocale),
    ]);
    let customStyleConfig = {};
    const dateFormatsConfig = {
        tsLocaleBasedDateFormats: { ...dateFormats },
        tsLocaleBasedStringsFormats: {
            invalidDate: strings.INVALID_DATE,
            null_value_placeholder_label: strings.NULL_VALUE_PLACEHOLDER_LABEL,
            other_value_placeholder_label: strings.OTHER_VALUE_PLACEHOLDER_LABEL,
            empty_value_placeholder_label: strings.EMPTY_VALUE_PLACEHOLDER_LABEL,
            unavailabe_column_sample_value: strings.UNAVAILABLE_COLUMN_SAMPLE_VALUE,
            quarter_of_year: strings.QUARTER_OF_YEAR,
            weekOfDay: strings.weekOfDay,
            monthOfYear: strings.monthOfYear,
        },
        tsDateConstants: {
            day_in_month_format: blinkConstants.DAY_IN_MONTH_FORMAT,
            day_in_quarter_format: blinkConstants.DAY_IN_QUARTER_FORMAT,
            day_in_year_format: blinkConstants.DAY_IN_YEAR_FORMAT,
            day_of_week_format: blinkConstants.DAY_OF_WEEK_FORMAT,
            month_in_quarter_format: blinkConstants.MONTH_IN_QUARTER_FORMAT,
            month_in_year_format: blinkConstants.MONTH_IN_YEAR_FORMAT,
            week_in_year_format: blinkConstants.WEEK_IN_YEAR_FORMAT,
            special_value_unavailable: blinkConstants.SPECIAL_VALUE_UNAVAILABLE,
        },
        tsDefinedCustomCalenders: customCalendars,
        defaultDataSourceId: DEFAULT_DATASOURCE_ID,
    };
    if (customStylingService?.getConfig()) {
        const { appBackground, appPanelColor, chartColorPalettes, disableColorRotation, chartFeatureToFontGuid, muzeFeatureToFontGuid, customFontFaces, defaultColors, } = customStylingService?.getConfig();
        const fontFacesCacheKey = JSON.stringify(customFontFaces);
        let fontFaces;
        if (fontFacesCache.has(fontFacesCacheKey)) {
            fontFaces = fontFacesCache.get(fontFacesCacheKey);
        }
        else {
            fontFaces = await getAllFontFaces(customFontFaces);
            fontFacesCache.set(fontFacesCacheKey, fontFaces);
        }
        const colorPallete = chartColorPalettes?.[0]?.colors &&
            chartColorPalettes?.[0]?.colors.length > 0
            ? chartColorPalettes
            : [{ colors: defaultColors }];
        customStyleConfig = {
            appBackground: appBackground?.color
                ? appBackground
                : {
                    color: blinkConstants.defaultAppBackground,
                },
            appPanelColor: appPanelColor?.color
                ? appPanelColor
                : {
                    color: blinkConstants.defaultAppPanelColor,
                },
            chartColorPalettes: colorPallete,
            numColorPalettes: getNumOfColorPalette(),
            disableColorRotation,
            chartFeatureToFontGuid,
            muzeFeatureToFontGuid,
            fontFaces,
        };
    }
    const userInfo = isTsHostedChart
        ? {
            userGuid: SessionService?.getUserGuid(),
            userProperties: getSelectedUserProperties(SessionService?.getUserProperties()),
        }
        : undefined;
    return {
        dateFormatsConfig,
        styleConfig: customStyleConfig,
        embedCssStyles,
        appOptions: {
            isMobile: false,
            isPrintMode,
            isDebugMode: debugMode,
            isLiveboardContext,
            isEmbedded,
            isSpotterContext,
            ...(queryMetrics && { queryMetrics }),
        },
        ...(userInfo && { userInfo }),
        releaseVersion,
        localeOptions: {
            locale,
            currencyLocale,
            sessionTimezone: timezone,
            quarterStartMonth: quarterStart,
            numberLocale: numberFormatLocale,
            dateLocale: dateFormatLocale,
        },
        appUrl: customChartAppUrl,
        chartAppAccessToken,
        initFlags: {
            ...blinkFlags,
            ...ldFlags,
            enableMuzeAiChat: {
                flagId: 'enableMuzeAiChat',
                flagValue: SessionService.enableMuzeAiChat(),
            },
        },
        invalidValuePlaceholders: {
            nullPlaceholder: getNullValuePlaceHolder(),
            emptyPlaceholder: getEmptyValuePlaceHolder(),
        },
    };
};
//# sourceMappingURL=app-config.util.js.map